/**
 * The shape of the whole app, as a graph.
 *
 * This is the layout behind the ambient background: every chapter of the story
 * and every verse in the library is a node, and the edges are relationships
 * that already exist in the data rather than decoration invented for the
 * picture —
 *
 *   chapter → chapter   the narrative spine, in order
 *   chapter → verse     the verse that chapter turns on (`chapter.verseId`)
 *   verse   → verse     verses sharing a theme, chained
 *
 * That last one is why the field has clusters: the thirty themes pull their
 * members together, so "Faith" and "Covenant" end up as visibly distinct knots
 * even though nothing labels them. The picture is true, which is the only
 * reason it is worth drawing at all — a random particle web would look the
 * same and mean nothing.
 *
 * The layout is computed once, on first use, and is fully deterministic: the
 * same seed, the same relaxation, the same result on every device and every
 * reload. Nothing here touches the DOM or the clock, so it is safe to import
 * from a test and assert on.
 *
 * Coordinates are normalised to 0–1. The canvas maps them to pixels, which is
 * what lets the same layout hold its shape on a phone and a desktop.
 */

import { CHAPTERS } from '../data/story.js';
import { VERSES } from '../data/verses.js';

/** Small, fast, and repeatable — the same PRNG the daily puzzle uses. */
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-1a, so a node's starting position comes from its own id. */
function hash(text) {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Where chapter `i` of 28 sits: a lazy S sweeping left to right.
 *
 * The story is the frame everything else hangs off, so it gets a shape rather
 * than a scatter, and it is pinned there through every relaxation pass. Left to
 * right because that is the direction time runs in every other part of the app.
 */
function spineAt(index, total) {
  const t = total > 1 ? index / (total - 1) : 0.5;
  return {
    x: 0.07 + 0.86 * t,
    y: 0.5 + 0.26 * Math.sin(t * Math.PI * 1.7 + 0.5),
  };
}

const RELAX_PASSES = 55;
const THEME_PULL = 0.05; // toward the centre of your theme
const CHAPTER_PULL = 0.075; // toward the chapter that quotes you
const MIN_GAP = 0.042; // below this, nodes shove each other apart
const EDGE = 0.03; // keep everything off the very rim

function buildGraph() {
  const nodes = [];
  const index = new Map();

  const add = (node) => {
    index.set(node.id, nodes.length);
    nodes.push(node);
  };

  CHAPTERS.forEach((chapter, i) => {
    const seat = spineAt(i, CHAPTERS.length);
    add({
      id: chapter.id,
      kind: 'chapter',
      // Bigger than a verse: twenty-eight of these carry the shape, and
      // ninety-six verses would otherwise drown them.
      weight: 1,
      ...seat,
    });
  });

  for (const verse of VERSES) {
    const random = mulberry32(hash(verse.id));
    add({
      id: verse.id,
      kind: 'verse',
      theme: verse.theme,
      weight: 0.45,
      x: EDGE + random() * (1 - EDGE * 2),
      y: EDGE + random() * (1 - EDGE * 2),
    });
  }

  const edges = [];
  const link = (a, b) => {
    const from = index.get(a);
    const to = index.get(b);
    if (from != null && to != null && from !== to) edges.push([from, to]);
  };

  // The spine.
  for (let i = 1; i < CHAPTERS.length; i++) link(CHAPTERS[i - 1].id, CHAPTERS[i].id);

  // Each chapter to the verse it turns on.
  for (const chapter of CHAPTERS) link(chapter.id, chapter.verseId);

  // Themes are chained rather than fully connected — a clique of nine would be
  // a solid blot at this opacity. The chaining itself happens after the layout
  // has settled, in `chainThemes`, because the order to chain them in depends
  // on where they ended up.
  const themes = new Map();
  for (const verse of VERSES) {
    if (!themes.has(verse.theme)) themes.set(verse.theme, []);
    themes.get(verse.theme).push(verse.id);
  }

  return { nodes, edges, index, themes, link };
}

/** Anything longer than this is a scratch across the picture, not a relationship. */
const MAX_EDGE = 0.3;

/**
 * Wire up each theme, nearest neighbour first.
 *
 * Chaining a theme in data order draws lines between verses that the layout
 * has put at opposite corners, and a dozen of those read as scratches on the
 * lens rather than as structure. Walking the cluster nearest-first gives a
 * short local path instead — the same relationship, drawn in the shape the
 * layout actually has.
 */
function chainThemes({ nodes, index, themes, link }) {
  for (const members of themes.values()) {
    if (members.length < 2) continue;

    const remaining = new Set(members);
    let current = members[0];
    remaining.delete(current);

    while (remaining.size) {
      const from = nodes[index.get(current)];
      let best = null;
      let bestDistance = Infinity;
      for (const id of remaining) {
        const to = nodes[index.get(id)];
        const distance = Math.hypot(to.x - from.x, to.y - from.y);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = id;
        }
      }
      if (bestDistance <= MAX_EDGE) link(current, best);
      remaining.delete(best);
      current = best;
    }
  }
}

/**
 * Settle the verses around the fixed spine.
 *
 * Not a general force-directed solver — three rules, run to a fixed pass count,
 * because a background does not need to converge, it needs to look composed and
 * be the same every time.
 */
function relax({ nodes, index, themes }) {
  const centroid = { x: 0, y: 0 };

  for (let pass = 0; pass < RELAX_PASSES; pass++) {
    // Pull each verse toward the middle of its theme.
    for (const members of themes.values()) {
      centroid.x = 0;
      centroid.y = 0;
      for (const id of members) {
        const node = nodes[index.get(id)];
        centroid.x += node.x;
        centroid.y += node.y;
      }
      centroid.x /= members.length;
      centroid.y /= members.length;

      for (const id of members) {
        const node = nodes[index.get(id)];
        node.x += (centroid.x - node.x) * THEME_PULL;
        node.y += (centroid.y - node.y) * THEME_PULL;
      }
    }

    // And toward the chapter that quotes it, which is what ties the clusters
    // back onto the spine instead of letting them float free.
    for (const chapter of CHAPTERS) {
      const anchor = nodes[index.get(chapter.id)];
      const verse = nodes[index.get(chapter.verseId)];
      if (!anchor || !verse) continue;
      verse.x += (anchor.x - verse.x) * CHAPTER_PULL;
      verse.y += (anchor.y - verse.y) * CHAPTER_PULL;
    }

    // Shove apart anything that has ended up on top of something else. All
    // pairs: 124 nodes is 7,626 comparisons, which is nothing done once.
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i];
        const b = nodes[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const distance = Math.hypot(dx, dy) || 0.0001;
        if (distance >= MIN_GAP) continue;

        const push = ((MIN_GAP - distance) / distance) * 0.5;
        const ox = dx * push;
        const oy = dy * push;
        // Chapters are the frame; they do not get moved by a verse.
        if (a.kind === 'verse') {
          a.x -= ox;
          a.y -= oy;
        }
        if (b.kind === 'verse') {
          b.x += ox;
          b.y += oy;
        }
      }
    }

    // Chapters are re-pinned every pass, and verses are kept off the rim.
    CHAPTERS.forEach((chapter, i) => {
      Object.assign(nodes[index.get(chapter.id)], spineAt(i, CHAPTERS.length));
    });
    for (const node of nodes) {
      if (node.kind !== 'verse') continue;
      node.x = Math.min(1 - EDGE, Math.max(EDGE, node.x));
      node.y = Math.min(1 - EDGE, Math.max(EDGE, node.y));
    }
  }
}

let cached = null;

/**
 * The settled graph — built on first use, then handed back as-is.
 *
 * Deliberately *not* computed at import time. Relaxing 124 nodes is about sixty
 * milliseconds of straight-line arithmetic, and sixty milliseconds spent before
 * first paint to arrange a background nobody has seen yet is sixty milliseconds
 * stolen from the thing they opened the app for. The canvas asks for this after
 * it has mounted, so the cost lands in a frame where nothing else is happening.
 */
export function layout() {
  if (cached) return cached;

  // Marked so the cost stays measurable from a test rather than being asserted
  // in a comment. It is meant to land *after* first contentful paint; if it
  // ever creeps back in front of it, something has started calling this during
  // render again.
  globalThis.performance?.mark?.('ba-layout-start');
  const graph = buildGraph();
  relax(graph);
  chainThemes(graph);
  // Prune whatever is still spanning the screen — a couple of the story spine's
  // own hops are long, and one hairline across the whole viewport reads as a
  // flaw in the glass.
  graph.edges = graph.edges.filter(([a, b]) => {
    const from = graph.nodes[a];
    const to = graph.nodes[b];
    return Math.hypot(to.x - from.x, to.y - from.y) <= MAX_EDGE;
  });

  // Each node drifts on its own slow ellipse. Seeded from the id so the motion
  // is as repeatable as the layout — two devices side by side stay in step.
  for (const node of graph.nodes) {
    const random = mulberry32(hash(`${node.id}:drift`));
    node.phase = random() * Math.PI * 2;
    node.speed = 0.05 + random() * 0.09; // radians per second — a slow minute
    node.orbit = 0.004 + random() * 0.007; // in normalised units
  }

  cached = { nodes: graph.nodes, edges: graph.edges };
  globalThis.performance?.measure?.('ba-layout', 'ba-layout-start');
  return cached;
}

/**
 * Which nodes are lit.
 *
 * A chapter lights when it has been finished; a verse lights when it has been
 * marked known. Returns a Set of node *indices* so the draw loop can test
 * membership without a string compare per node per frame.
 */
export function litNodes(story = {}, learning = {}) {
  const lit = new Set();
  layout().nodes.forEach((node, i) => {
    if (node.kind === 'chapter' ? story[node.id]?.done : learning[node.id] === 'known') {
      lit.add(i);
    }
  });
  return lit;
}
